import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Select from "react-select";
import { comma } from "@/utils";
import { useLazyGetRentPriceTableItemsQuery } from "@/redux/metaApi";
import { usePutContractRentPriceItemMutation } from "@/redux/contractApi";

export const BillingSettingPopup = ({ layer = true, onClose, formData, setFormData, selectedContractIndex }) => {
    const { rentCorpId, contracts, contractInsurance } = formData;
    const [activeTab, setActiveTab] = useState("billing"); // "billing" / "price"
    const [selectedPriceTableId, setSelectedPriceTableId] = useState(null);
    const [priceTableDetails, setPriceTableDetails] = useState([]); // 요금표 상세 데이터
    const [groupedData, setGroupedData] = useState({}); // 그룹화된 데이터: { [groupKey]: [...rows] }
    const [activeGroup, setActiveGroup] = useState(""); // 현재 활성화된 그룹
    const [selectedPriceTableItem, setSelectedPriceTableItem] = useState(null);

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    // Lazy hook: getRentPriceTableItems API 호출
    const [triggerPriceTableItems] = useLazyGetRentPriceTableItemsQuery();
    // putContractRentPriceItem API hook
    const [putContractRentPriceItem] = usePutContractRentPriceItemMutation();

    // formData.contractInsurance.chargeMethod에 따라 API 호출 처리
    useEffect(() => {
        const chargeMethod = formData.contractInsurance?.chargeMethod;
        // "INPERSON"이면 activeTab은 "price"로 설정하고 API는 호출하지 않음
        if (chargeMethod === "INPERSON") {
            setActiveTab("price");
            setPriceTableDetails([]);
            setGroupedData({});
            setActiveGroup("");
            setSelectedPriceTableItem(null);
            return;
        }
        // "INSURANCE_BASE"이면 insurance_corp_id를 파라미터로 전달
        if (chargeMethod === "INSURANCE_BASE") {
            const insuranceCorpId = formData.contractInsurance?.insuranceCorpId;
            if (insuranceCorpId) {
                triggerPriceTableItems({ insurance_corp_id: insuranceCorpId })
                    .unwrap()
                    .then((res) => {
                        if (res.data) {
                            setPriceTableDetails(res.data);
                            groupData(res.data);
                        }
                    })
                    .catch((err) => {
                        console.error("요금표 조회 실패:", err);
                    });
            }
        } else if (chargeMethod === "RENT_PRICE") {
            triggerPriceTableItems({ rent_price_table_id: formData.contractInsurance?.rentPriceTableId })
                .unwrap()
                .then((res) => {
                    if (res.data) {
                        setPriceTableDetails(res.data);
                        groupData(res.data);
                    }
                })
                .catch((err) => {
                    console.error("요금표 조회 실패:", err);
                });
        }
    }, [formData.contractInsurance?.chargeMethod, formData.contractInsurance?.insuranceCorpId, triggerPriceTableItems]);

    // 그룹화 함수: carGubun의 공백 제거 후 그룹화
    const groupData = (details) => {
        const groups = {};
        details.forEach((item) => {
            const groupKey = item.carGubun.replace(/\s+/g, "");
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(item);
        });
        setGroupedData(groups);
        const keys = Object.keys(groups);
        if (keys.length > 0) {
            setActiveGroup(keys[0]);
        } else {
            setActiveGroup("");
        }
    };

    // Select의 onChange 핸들러
    const handlePriceTableSelect = (option) => {
        setSelectedPriceTableId(option.value);
        // 초기화
        setGroupedData({});
        setActiveGroup("");
        setPriceTableDetails([]);
        setSelectedPriceTableItem(null);
    };

    const handleRegister = async () => {
        // 선택된 요금표 항목이 없으면 토스트 메시지 출력
        if (!selectedPriceTableItem) {
            setFormData((prev) => ({ ...prev, toastMsg: "요금표 항목을 선택해주세요." }));
            return;
        }
        // putContractRentPriceItem API 호출
        if (contracts && contracts[selectedContractIndex]) {
            const contractGroupId = formData.id;
            const contractId = contracts[selectedContractIndex].id;
            const rentPriceItemId = selectedPriceTableItem.id;
            console.log("contractGroupId:", contractGroupId, "contractId:", contractId, "rentPriceItemId:", rentPriceItemId);
            try {
                await putContractRentPriceItem({ contractGroupId, contractId, rentPriceItemId });
            } catch (e) {
                setFormData((prev) => ({ ...prev, toastMsg: "요금표 저장에 실패했습니다." }));
                return;
            }
        }
        // 해당 계약(contract) 내 billingInfo 객체에 rentPrice 항목을 추가
        setFormData((prev) => ({
            ...prev,
            contracts: prev.contracts?.map((contract, idx) =>
                idx === selectedContractIndex
                    ? {
                          ...contract,
                          billingInfo: {
                              ...(contract?.billingInfo || {}),
                              rentPrice: selectedPriceTableItem,
                          },
                      }
                    : contract
            ),
        }));
        onClose();
    };

    // 그룹별 토글 버튼 렌더링
    const renderToggleButtons = () => {
        return Object.keys(groupedData).map((groupKey) => (
            <button
                key={groupKey}
                type="button"
                className={`btn-toggle ${activeGroup === groupKey ? "active" : ""}`}
                onClick={() => setActiveGroup(groupKey)}
            >
                {groupKey}
            </button>
        ));
    };

    // 활성 그룹에 해당하는 데이터를 테이블 행으로 렌더링
    const renderTableRows = () => {
        if (!activeGroup || !groupedData[activeGroup]) return null;
        return groupedData[activeGroup].map((item) => {
            const price0 = item.rentPriceItemDetails.find((d) => d.endMinute === 600)?.price;
            const price1 = item.rentPriceItemDetails.find((d) => d.endMinute === 720)?.price;

            const price12 = item.rentPriceItemDetails.find((d) => d.endMinute === 4320)?.price;
            const price34 = item.rentPriceItemDetails.find((d) => d.endMinute === 7200)?.price;
            const price56 = item.rentPriceItemDetails.find((d) => d.endMinute === 10080)?.price;
            const price7up = item.rentPriceItemDetails.find((d) => d.startMinute === 10080)?.price;
            return (
                <tr key={item.id}>
                    <td>
                        <div className="cell" data-align="center">
                            <div className="input-checkbox">
                                <div className="radio">
                                    <input
                                        type="radio"
                                        id={`price-${item.id}`}
                                        name="priceTableSelect"
                                        onChange={() => setSelectedPriceTableItem(item)}
                                        checked={selectedPriceTableItem && selectedPriceTableItem.id === item.id}
                                    />
                                    <label htmlFor={`price-${item.id}`} style={{height: "24px"}}></label>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{item.carGubun.replace(/\s+/g, "")}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{item.modelTitle}</span>
                        </div>
                    </td>

                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price0 !== undefined ? comma(price0) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price1 !== undefined ? comma(price1) : "-"}</span>
                        </div>
                    </td>

                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price12 !== undefined ? comma(price12) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price34 !== undefined ? comma(price34) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price56 !== undefined ? comma(price56) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price7up !== undefined ? comma(price7up) : "-"}</span>
                        </div>
                    </td>
                </tr>
            );
        });
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup service-popup" style={{width: "1200px"}}>
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">청구기준 설정</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    {/* <div className="tab-list">
                        <button
                            type="button"
                            className={`btn-toggle ${activeTab === "billing" ? "active" : ""}`}
                            onClick={() => setActiveTab("billing")}
                        >
                            청구기준
                        </button>
                        <button
                            type="button"
                            className={`btn-toggle ${activeTab === "price" ? "active" : ""}`}
                            onClick={() => setActiveTab("price")}
                        >
                            기준가 설정
                        </button>
                    </div> */}

                    {activeTab === "billing" && (
                        <>
                            <div className="table-container" style={{ marginTop: "0" }}>
                                {/* <div className="table-filter">
                                    <div className="filter-row">
                                        <div className="filter-item">
                                            <div className="item-head">
                                                <span className="head-tit">요금표 선택</span>
                                            </div>
                                            <div className="item-content">
                                                <div className="item-input">
                                                    <Select
                                                        options={rentPriceTableOptions}
                                                        className="custom-select"
                                                        classNamePrefix="custom-select"
                                                        isSearchable={false}
                                                        placeholder="요금표 선택"
                                                        value={rentPriceTableOptions.find((opt) => opt.value === selectedPriceTableId)}
                                                        onChange={handlePriceTableSelect}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}

                                {priceTableDetails && priceTableDetails.length > 0 && (
                                    <div className="table-content">
                                        <div className="table-option">
                                            <div className="table-caption">
                                                <div className="toggle-list">{renderToggleButtons()}</div>
                                            </div>
                                        </div>

                                        <div className="table-wrapper">
                                            <div className="table">
                                                <table>
                                                    <colgroup>
                                                        <col width="7%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                        <col width="12%"/>
                                                    </colgroup>
                                                    <thead>
                                                    <tr>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">선택</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">차종</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">대표모델</div>
                                                            </div>
                                                        </th>

                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">~10시간</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">10시간~12시간</div>
                                                            </div>
                                                        </th>

                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">12시간~2일</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">3일~4일</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">5일~6일</div>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="cell">
                                                                <div className="cell-tit">7일이상</div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>{renderTableRows()}</tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === "price" && (
                        <>
                            <div className="content-textbox">
                                <div className="item-list">
                                    <div className="item">
                                        <span className="item-desc">업체의 요금 기준가로 변경 합니다.</span>
                                    </div>
                                    <div className="item">
                                        <span className="item-input">
                                            <input type="text" placeholder="기준가를 입력해주세요." />
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="content-description">
                                <p>
                                    <em>청구 기준가를 업체의 기준으로 변경 시 사업자의 요금표 파일을 첨부하여 청구 해주셔야 합니다.</em>
                                </p>
                                <p>요금표는 사업자 정보 관리에서 파일을 업로드 해주세요.</p>
                            </div>
                        </>
                    )}
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-confirm" onClick={handleRegister}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
